import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, FileText } from 'lucide-react';

const scopeStandards = [
  'ISO 9001:2015 — Quality Management Systems',
  'ISO/IEC 20000-1:2018 — Information Technology Service Management Systems',
  'ISO/IEC 27001:2022 — Information Security Management Systems',
  'ISO/IEC 42001:2023 — Artificial Intelligence Management Systems',
];

const definitions = [
  { term: 'Management System', text: 'Interrelated or interacting elements used to establish policies, objectives, and processes.' },
  { term: 'Nonconformity', text: 'Non-fulfilment of a requirement.' },
  { term: 'Major Nonconformity', text: 'A nonconformity that affects the capability of the management system to achieve intended results.' },
  { term: 'Minor Nonconformity', text: 'A nonconformity that does not affect the capability of the management system to achieve intended results.' },
  { term: 'Corrective Action', text: 'Action to eliminate the cause of nonconformity and prevent recurrence.' },
];

const feeItems = [
  'Fees are defined in the quotation and may be adjusted with written notice.',
  'An audit day consists of 8 hours of audit activity.',
  'Initial registration fees are due upon application; ongoing fees follow the quotation schedule.',
  'Additional fees apply for unscheduled audits, scope changes, incidents requiring investigation, and late cancellations (within 2 weeks of the audit date).',
  'Certificates will not be issued until all fees are paid.',
  'All fees are subject to applicable taxes.',
];

const generalConditions = [
  'Provide all information necessary for QSC to complete the audit program.',
  'Permit QSC to publicize your certification status.',
  'Address all nonconformities within specified timeframes.',
  'Conduct internal audits and management reviews at least once per year.',
  'Allow surveillance audits per the planned schedule.',
  'Notify QSC within 7 days of serious incidents (fatality, data breach, service outage, AI harm event), regulatory prosecution, or major changes to the management system, ownership, or operations.',
];

const stage1 = [
  'Review of documented management system',
  'Evaluation of site conditions',
  'Assessment of readiness for Stage 2',
  'Confirmation of scope, processes, risks, and legal obligations',
  'Planning of Stage 2 audit',
];

const stage2 = [
  'Evaluation of implementation and effectiveness',
  'Review of performance monitoring, legal compliance, internal audits, management review, and operational controls',
  'Verification of competence, processes, and objective evidence',
];

const surveillanceItems = [
  'Internal audits and management reviews',
  'Treatment of complaints',
  'Effectiveness of corrective actions',
  'Legal compliance',
  'Operational control',
  'Continual improvement activities',
];

const suspensionReasons = [
  'Widespread failure to meet standard requirements',
  'Failure to permit audits',
  'Failure to notify QSC of significant changes',
  'Misuse of marks',
  'Falsification of records',
  'Non-payment of fees',
  'Failure to respond to corrective actions within 30 days',
  'Voluntary suspension requests',
  'Non-response to QSC communications',
];

const cancellationReasons = [
  'You choose not to renew',
  'You cease trading or enter insolvency',
  'You voluntarily cancel certification',
];

const TermsPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-gradient-to-r from-[#003f6d] via-[#004a85] to-[#003f6d] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">Terms &amp; Conditions</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-xl text-gray-200 max-w-3xl">
            These Terms &amp; Conditions govern all certification activities performed by QSC America
            and apply to every applicant and certified client.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Scope</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              QSC America (&ldquo;QSC&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;) provides accredited conformity assessment
              services for the following management system standards:
            </p>
            <ul className="space-y-2 mb-4">
              {scopeStandards.map((s) => (
                <li key={s} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              These Terms &amp; Conditions govern all certification activities performed by QSC America.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Definitions</h2>
            <ul className="space-y-2">
              {definitions.map((d) => (
                <li key={d.term} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span><strong>{d.term}:</strong> {d.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Application for Certification</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Upon receipt of your completed application, QSC will issue a quotation outlining audit
              criteria, audit days, and fees.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Certification activities begin once QSC receives:</p>
            <ul className="space-y-2 mb-3">
              <li className="flex items-start gap-2 text-gray-700"><ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" /><span>A signed application</span></li>
              <li className="flex items-start gap-2 text-gray-700"><ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" /><span>Required payment</span></li>
            </ul>
            <p className="text-gray-700 leading-relaxed">An audit team will then be assigned.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Fees</h2>
            <ul className="space-y-2">
              {feeItems.map((f) => (
                <li key={f} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. General Conditions</h2>
            <p className="text-gray-700 leading-relaxed mb-3">To obtain and maintain certification, you agree to:</p>
            <ul className="space-y-2 mb-4">
              {generalConditions.map((g) => (
                <li key={g} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              QSC retains full authority for certification decisions, including granting, maintaining,
              renewing, extending, reducing, suspending, and withdrawing certification.
            </p>
            <Card className="border-l-4 border-l-[#d4af37]">
              <CardContent className="p-4">
                <p className="text-gray-700 italic">
                  &ldquo;You must use QSC America certification marks only in accordance with the QSC
                  America Rules for Use of Certification Marks, available upon request.&rdquo;
                </p>
              </CardContent>
            </Card>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Confidentiality</h2>
            <p className="text-gray-700 leading-relaxed">
              QSC ensures confidentiality of all client information except where disclosure is required
              by law or accreditation rules. You will be informed of any legally required disclosures.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Changes</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You must notify QSC in writing of intended changes to:</p>
            <ul className="space-y-2 mb-3">
              {['Legal or organizational status', 'Ownership', 'Sites and locations', 'Scope of operations', 'Major management system changes'].map((c) => (
                <li key={c} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              QSC will determine whether additional audit activity is required. Failure to notify may
              result in suspension.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Initial Audit</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Initial certification consists of:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <Card className="border-t-4 border-t-[#003f6d]">
                <CardContent className="p-5">
                  <h3 className="font-bold text-gray-900 mb-3">Stage 1 Audit</h3>
                  <ul className="space-y-2">
                    {stage1.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-gray-600 text-sm">
                        <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-[#c1272d]">
                <CardContent className="p-5">
                  <h3 className="font-bold text-gray-900 mb-3">Stage 2 Audit</h3>
                  <ul className="space-y-2">
                    {stage2.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-gray-600 text-sm">
                        <ArrowRight className="w-4 h-4 text-[#c1272d] flex-shrink-0 mt-1" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
            <p className="text-gray-700 leading-relaxed">
              All records required for the management system must be available to the audit team.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Certification Decision</h2>
            <p className="text-gray-700 leading-relaxed">
              Certification is granted when QSC determines that all requirements are met. You will be
              listed in the QSC America certification directory. Certificates remain QSC property and
              must be returned upon request.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Surveillance</h2>
            <p className="text-gray-700 leading-relaxed mb-3">Surveillance audits confirm continued conformity, including:</p>
            <ul className="space-y-2 mb-4">
              {surveillanceItems.map((s) => (
                <li key={s} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">Surveillance occurs at least once per calendar year.</p>
            <Card className="border-l-4 border-l-[#d4af37]">
              <CardContent className="p-4">
                <p className="text-gray-700 italic">
                  &ldquo;QSC America reserves the right to conduct unannounced audits when required by
                  accreditation rules.&rdquo;
                </p>
              </CardContent>
            </Card>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Renewal of Registration</h2>
            <p className="text-gray-700 leading-relaxed">
              Certification cycles run for three years. A full re-audit must occur before expiry. A new
              quotation will be issued six months prior to expiration.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Extension or Reduction of Scope</h2>
            <p className="text-gray-700 leading-relaxed">
              Scope extensions require a new application and may require additional audit time. Scope
              reductions must be reported immediately and may require audit verification.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Publicity and Use of Marks</h2>
            <p className="text-gray-700 leading-relaxed">
              You may publicize certification status and use QSC certification marks only as permitted.
              Marks may not be applied to products or packaging. Statements must not imply product
              certification.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Misuse of Certificate</h2>
            <p className="text-gray-700 leading-relaxed">
              Incorrect use of certificates or marks may result in suspension, withdrawal, legal action,
              or public notice.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">15. Suspension of Certificate</h2>
            <p className="text-gray-700 leading-relaxed mb-3">Certificates may be suspended for:</p>
            <ul className="space-y-2 mb-4">
              {suspensionReasons.map((s) => (
                <li key={s} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#c1272d] flex-shrink-0 mt-1" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              If suspended, you must immediately cease use of certification marks.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">16. Withdrawal of Certificate</h2>
            <p className="text-gray-700 leading-relaxed">
              Certificates may be withdrawn if suspension conditions are not resolved or if the
              certificate expires. You will receive written notice and may appeal within 7 days.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">17. Cancellation of Certificate</h2>
            <p className="text-gray-700 leading-relaxed mb-3">Cancellation may occur if:</p>
            <ul className="space-y-2 mb-3">
              {cancellationReasons.map((c) => (
                <li key={c} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">No fees are refundable.</p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">18. Consequences of Suspension, Withdrawal, or Cancellation</h2>
            <p className="text-gray-700 leading-relaxed">
              You must immediately cease use of certification marks and remove all promotional
              materials referencing certification.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">19. Appeals, Disputes, and Complaints</h2>
            <p className="text-gray-700 leading-relaxed">
              Complaints or appeals must follow QSC&rsquo;s Complaint &amp; Appeal Procedure. Appeals
              must be submitted within 7 days of receiving a suspension, withdrawal, or nonconformity
              notice. See our{' '}
              <Link to="/certification/public-information" className="text-[#003f6d] font-medium hover:underline">
                Public Certification Information
              </Link>{' '}
              page for how to submit a complaint or appeal.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">20. Directory of Certified Companies</h2>
            <p className="text-gray-700 leading-relaxed">
              QSC maintains a public directory of certified entities, including scope and locations.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">21. Accreditation Body Witnessed Audits</h2>
            <p className="text-gray-700 leading-relaxed">
              You must permit accreditation body representatives to witness audits when requested by QSC.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">22. Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              QSC audits are based on sampling and do not guarantee full conformity. QSC is not
              responsible for regulatory or legal non-compliance by the client. Except for death or
              personal injury caused by QSC negligence, QSC is not liable for indirect, special, or
              consequential losses.
            </p>
            <p className="text-gray-700 leading-relaxed">
              QSC reserves the right to amend these Terms &amp; Conditions at any time.
            </p>
          </div>

          <Card className="bg-[#003f6d]/5 border-none">
            <CardContent className="p-6 flex items-start gap-4">
              <FileText className="w-8 h-8 text-[#003f6d] flex-shrink-0 mt-1" />
              <div>
                <p className="text-gray-800 font-medium">Questions about these Terms &amp; Conditions?</p>
                <p className="text-gray-600 text-sm mt-1">
                  Contact us at{' '}
                  <a href="mailto:info@qscamerica.com" className="text-[#003f6d] font-medium hover:underline">
                    info@qscamerica.com
                  </a>{' '}
                  or visit our{' '}
                  <Link to="/contact" className="text-[#003f6d] font-medium hover:underline">
                    Contact page
                  </Link>.
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default TermsPage;
